import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Building2, Loader2, CheckCircle, AlertCircle, User, Lock, FileText, ChevronDown } from 'lucide-react';
import { TERMS_TEXT } from '../lib/termsContent';


const API = process.env.REACT_APP_BACKEND_URL + '/api';


export default function ClientSignupPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const termsContentRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchInvite();
  }, [token]);

  const fetchInvite = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/client-invite/${token}`);
      setInvite(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired invitation link');
    } finally {
      setLoading(false);
    }
  };

  const handleTermsScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 30) {
      setScrolledToBottom(true);
    }
  };

  const handleAcceptTerms = () => {
    setTermsAccepted(true);
    setTermsModalOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!termsAccepted) {
      toast.error('Please read and accept the Terms & Conditions');
      return;
    }
    if (!formData.name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    // Strong password: min 8 chars, must include letter, number, and symbol
    const pw = formData.password || '';
    const hasLetter = /[A-Za-z]/.test(pw);
    const hasNumber = /\d/.test(pw);
    const hasSymbol = /[^A-Za-z0-9]/.test(pw);
    if (pw.length < 8 || !hasLetter || !hasNumber || !hasSymbol) {
      toast.error('Password must be at least 8 characters and include a letter, a number, and a symbol.');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/client-signup`, {
        invite_token: token,
        name: formData.name,
        password: formData.password
      });

      localStorage.setItem('token', response.data.access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      
      setSuccess(true);
      toast.success('Account created successfully!');
      
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-600 to-blue-800">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-600 to-blue-800 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Invalid Invitation</h2>
            <p className="text-slate-500 mb-6">{error}</p>
            <Link to="/login">
              <Button>Go to Login</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-600 to-blue-800 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Welcome!</h2>
            <p className="text-slate-500 mb-2">Your account has been created for <strong>{invite.company_name}</strong></p>
            <p className="text-sm text-slate-400">Redirecting to dashboard...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-600 to-blue-800 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 p-3 bg-blue-100 rounded-full w-fit">
            <Building2 className="w-8 h-8 text-blue-600" />
          </div>
          <CardTitle className="text-xl">Cash Flow Timing Planner</CardTitle>
          <CardDescription>
            Create your account to get started
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Invitation Details */}
          <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Company</p>
                <p className="font-semibold">{invite.company_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Email</p>
                <p className="font-medium">{invite.email}</p>
              </div>
            </div>
          </div>

          {/* Signup Form */}
          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
            <div className="space-y-2">
              <Label>Your Name</Label>
              <Input
                type="text"
                placeholder="John Smith"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                autoComplete="off"
                data-testid="client-signup-name"
              />
            </div>

            <div className="space-y-2">
              <Label>Password</Label>
              <Input
                type="password"
                placeholder="Min 8 chars with letter, number & symbol"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={8}
                autoComplete="new-password"
                data-testid="client-signup-password"
              />
              {/* Strength checklist */}
              <ul className="text-xs space-y-1 mt-1">
                <li className={(formData.password || '').length >= 8 ? 'text-green-600' : 'text-slate-500'}>
                  {(formData.password || '').length >= 8 ? '✓' : '○'} At least 8 characters
                </li>
                <li className={/[A-Za-z]/.test(formData.password || '') ? 'text-green-600' : 'text-slate-500'}>
                  {/[A-Za-z]/.test(formData.password || '') ? '✓' : '○'} Contains a letter
                </li>
                <li className={/\d/.test(formData.password || '') ? 'text-green-600' : 'text-slate-500'}>
                  {/\d/.test(formData.password || '') ? '✓' : '○'} Contains a number
                </li>
                <li className={/[^A-Za-z0-9]/.test(formData.password || '') ? 'text-green-600' : 'text-slate-500'}>
                  {/[^A-Za-z0-9]/.test(formData.password || '') ? '✓' : '○'} Contains a symbol (e.g. !@#$%)
                </li>
              </ul>
            </div>

            <div className="space-y-2">
              <Label>Confirm Password</Label>
              <Input
                type="password"
                placeholder="Confirm your password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
                autoComplete="new-password"
                data-testid="client-signup-confirm"
              />
            </div>

            {/* Terms & Conditions */}
            <div className="border rounded-lg p-3 space-y-2">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  readOnly
                  className="mt-1 accent-primary cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    if (!termsAccepted) {
                      setScrolledToBottom(false);
                      setTermsModalOpen(true);
                    } else {
                      setTermsAccepted(false);
                    }
                  }}
                  data-testid="terms-checkbox"
                />
                <div className="text-sm">
                  <span className="text-slate-700">I have read and agree to the </span>
                  <button
                    type="button"
                    onClick={() => {
                      setScrolledToBottom(false);
                      setTermsModalOpen(true);
                    }}
                    className="text-blue-600 hover:underline font-medium"
                    data-testid="terms-link"
                  >
                    Terms of Use, Disclaimer and Privacy Notice
                  </button>
                </div>
              </div>
              {termsAccepted && (
                <div className="flex items-center gap-1.5 text-green-600">
                  <CheckCircle className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium">Terms accepted</span>
                </div>
              )}
            </div>

            <Button 
              type="submit"
              disabled={submitting || !termsAccepted}
              className="w-full gap-2"
              data-testid="client-signup-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              Create Account
            </Button>

            <p className="text-xs text-slate-400 text-center">
              Already have an account? <Link to="/login" className="text-blue-600 hover:underline">Login here</Link>
            </p>
          </form>
        </CardContent>
      </Card>

      {/* Terms & Conditions Modal */}
      <Dialog open={termsModalOpen} onOpenChange={setTermsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-3 border-b">
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <FileText className="w-5 h-5 text-primary" />
              Terms of Use, Disclaimer and Privacy Notice
            </DialogTitle>
          </DialogHeader>
          
          <div className="relative flex-1 overflow-hidden">
            <div 
              ref={termsContentRef}
              onScroll={handleTermsScroll}
              className="overflow-y-auto px-6 py-4 h-full max-h-[55vh]"
              data-testid="terms-scroll-area"
            >
              <div className="prose prose-sm max-w-none">
                {TERMS_TEXT.split('\n').map((line, i) => {
                  const trimmed = line.trim();
                  if (!trimmed) return <div key={i} className="h-3" />;
                  
                  // Main title
                  if (trimmed === 'Terms of Use, Disclaimer and Privacy Notice') {
                    return <h2 key={i} className="text-lg font-bold text-slate-900 mb-1">{trimmed}</h2>;
                  }
                  // Company info lines
                  if (trimmed.startsWith('EYB Consulting') || trimmed.startsWith('ABN') || trimmed.startsWith('PO Box') || trimmed.startsWith('Effective date')) {
                    return <p key={i} className="text-xs text-slate-500 leading-tight">{trimmed}</p>;
                  }
                  // Section headers (numbered)
                  if (/^\d+\.\s/.test(trimmed) && !trimmed.startsWith('10.')) {
                    return <h3 key={i} className="text-sm font-bold text-slate-800 mt-4 mb-1">{trimmed}</h3>;
                  }
                  // Sub-section headers (10.1, 10.2, etc.)
                  if (/^\d+\.\d+\s/.test(trimmed)) {
                    return <h4 key={i} className="text-sm font-semibold text-slate-700 mt-3 mb-1">{trimmed}</h4>;
                  }
                  // Bullet points
                  if (trimmed.startsWith('- ')) {
                    return <p key={i} className="text-sm text-slate-600 pl-4 leading-relaxed">&bull; {trimmed.substring(2)}</p>;
                  }
                  // Regular text
                  return <p key={i} className="text-sm text-slate-600 leading-relaxed">{trimmed}</p>;
                })}
              </div>
            </div>
            
            {/* Scroll indicator */}
            {!scrolledToBottom && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-white via-white/90 to-transparent h-16 flex items-end justify-center pb-2 pointer-events-none">
                <div className="flex items-center gap-1 text-xs text-slate-500 animate-bounce pointer-events-auto">
                  <ChevronDown className="w-4 h-4" />
                  <span>Scroll to read all terms</span>
                  <ChevronDown className="w-4 h-4" />
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="px-6 py-4 border-t bg-slate-50">
            <Button 
              variant="outline" 
              onClick={() => setTermsModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAcceptTerms}
              disabled={!scrolledToBottom}
              className="gap-2"
              data-testid="accept-terms-btn"
            >
              <CheckCircle className="w-4 h-4" />
              {scrolledToBottom ? 'I Agree' : 'Please read all terms'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
